import {
  Box,
  Flex,
  HStack,
  Link,
  Drawer,
  Button,
  Portal,
  CloseButton,
  Icon,
  VStack,
} from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import { ColorModeButton } from './ui/color-mode';
import { Menu, LogOut } from 'lucide-react';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useLogout } from '@/hooks/useLogout';

export type NavItem = {
  title: string;
  path: string;
};

type PropTypes = {
  navItems: Array<NavItem>;
};

const HamburgerMenu = ({ navItems }: PropTypes) => {
  const { data: isAuthenticated } = useAuthStatus();
  const { logout, isLoading } = useLogout();

  return (
    <Drawer.Root>
      <Drawer.Trigger asChild>
        <Button variant="outline" size="sm">
          <Icon>
            <Menu />
          </Icon>
        </Button>
      </Drawer.Trigger>
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.Header></Drawer.Header>
            <Drawer.Body>
              <VStack alignItems="start" gap="3" width="100%">
                {navItems.map(item => {
                  return (
                    <Drawer.Context key={item.path}>
                      {({ setOpen }) => (
                        <Link
                          asChild
                          href={item.path}
                          fontWeight="medium"
                          fontSize="lg"
                          _hover={{
                            textDecoration: 'underline',
                          }}
                          onClick={() => setOpen(false)}
                        >
                          <NavLink to={item.path}>{item.title}</NavLink>
                        </Link>
                      )}
                    </Drawer.Context>
                  );
                })}
                {isAuthenticated && (
                  <Drawer.Context>
                    {({ setOpen }) => (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          logout();
                          setOpen(false);
                        }}
                        loading={isLoading}
                        loadingText="Loggar ut..."
                        width="100%"
                      >
                        <Icon>
                          <LogOut />
                        </Icon>
                        Logga ut
                      </Button>
                    )}
                  </Drawer.Context>
                )}
              </VStack>
            </Drawer.Body>
            <Drawer.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Drawer.CloseTrigger>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};

const Navbar = ({ navItems }: PropTypes) => {
  const { data: isAuthenticated } = useAuthStatus();
  const { logout, isLoading } = useLogout();

  return (
    <Box as="nav" py={4} borderBottomWidth="1px" borderColor="border" bg="bg" shadow="sm">
      <Flex
        justify="space-between"
        align="center"
        width="100%"
        maxW="breakpoint-2xl"
        px={{ base: 4, md: 6 }}
        mx="auto"
      >
        <Flex align="center" gap={10}>
          <Link asChild _hover={{ textDecoration: 'none' }}>
            <NavLink to="/">
              <img src="/logo.svg" alt="Palatium Fastigheter AB" height="50" />
            </NavLink>
          </Link>

          <HStack
            gap={8}
            align="center"
            display={{
              mdDown: 'none',
              md: 'flex',
            }}
          >
            {navItems.map(item => (
              <Link
                fontSize="md"
                asChild
                textDecoration="none"
                key={item.title}
                fontWeight="medium"
                color="fg.muted"
                _hover={{
                  color: 'fg',
                }}
              >
                <NavLink
                  to={item.path}
                  style={({ isActive }) => ({
                    color: isActive ? 'var(--chakra-colors-blue-fg)' : undefined,
                  })}
                >
                  {item.title}
                </NavLink>
              </Link>
            ))}
          </HStack>
        </Flex>
        <HStack gap={3}>
          {isAuthenticated && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => logout()}
              loading={isLoading}
              loadingText="Loggar ut..."
              display={{
                mdDown: 'none',
                md: 'flex',
              }}
            >
              <Icon>
                <LogOut />
              </Icon>
              Logga ut
            </Button>
          )}
          <ColorModeButton />
          <Box
            display={{
              mdDown: 'inline',
              md: 'none',
            }}
          >
            <HamburgerMenu navItems={navItems} />
          </Box>
        </HStack>
      </Flex>
    </Box>
  );
};

export default Navbar;
